/**
 * domains/ai/thesis2/schema.ts — MER Algo Footprints v7.1H
 *
 * Formal Zod v3 schemas — the single authoritative contract for:
 *   A. RawAIThesisSchema  — what the AI must return (nested)
 *   B. StructuredThesisSchema — the final assembled object
 *
 * SCHEMA_VERSION must be bumped whenever the shape of either schema changes.
 *
 * Type discipline:
 *   RawAIThesis = z.infer<typeof RawAIThesisSchema>
 *   AssembledThesis = z.infer<typeof StructuredThesisSchema>
 *   The StructuredThesis interface in types.ts is kept for ergonomics and
 *   must remain structurally compatible — enforced by the assignability check
 *   in types.ts.
 */

import { z } from 'zod';

export const SCHEMA_VERSION = 'thesis2-schema-v1.1';

// ─── Shared primitives ────────────────────────────────────────────────────────

const ThesisStateSchema = z.enum([
  'ACTIONABLE', 'DEVELOPING', 'WAIT', 'HIGH_RISK', 'INVALIDATED',
]);

const ActionPostureSchema = z.enum([
  'LONG', 'SHORT', 'NEUTRAL', 'REDUCE_RISK', 'NO_TRADE',
]);

const ThesisBiasSchema = z.enum([
  'bullish', 'bearish', 'neutral', 'mixed',
]);

const SetupClassSchema = z.enum([
  'breakout_long', 'breakout_short',
  'reversion_long', 'reversion_short',
  'continuation_long', 'continuation_short',
  'sweep_reversal_long', 'sweep_reversal_short',
  'wait', 'unknown',
]);

const ChangeTypeSchema = z.enum([
  'NO_MATERIAL_CHANGE', 'UPGRADE', 'DOWNGRADE', 'INVALIDATION',
  'BIAS_FLIP', 'RISK_INCREASE', 'RISK_DECREASE', 'TRIGGER_APPROACHING',
]);

const ValidationStatusSchema = z.enum([
  'AI_VALIDATED', 'AI_REPAIRED', 'FALLBACK', 'GATE_DOWNGRADED',
]);

// Helpers
const score    = z.number().int().min(0).max(100);
const nonEmpty = z.string().min(1);    // truly required, non-blank
const strArr   = z.array(z.string()).max(10);

// ─── A. Raw AI response schema (nested) ───────────────────────────────────────
//
// This is what the model must return. Every field path here must be reflected
// in assembleThesis() in pipeline.ts.

export const RawAIThesisSchema = z.object({
  // Top-level scalars
  thesis_state:         ThesisStateSchema,
  action_posture:       ActionPostureSchema,
  bias:                 ThesisBiasSchema,
  conviction:           score,
  actionability:        score,
  setup_classification: SetupClassSchema,
  setup_name:           nonEmpty,
  summary:              nonEmpty,
  decision:             nonEmpty,

  entry_plan: z.object({
    entry_trigger:       nonEmpty,
    entry_price_hint:    z.string().nullable().optional(),
    confirmation_needed: nonEmpty,
    timing_note:         z.string().nullable().optional(),
  }),

  risk_plan: z.object({
    invalidation_level:  nonEmpty,
    invalidation_reason: nonEmpty,
    primary_risk_note:   nonEmpty,
  }),

  targets: z.object({
    target_1:     z.string().nullable().optional(),
    target_2:     z.string().nullable().optional(),
    target_logic: nonEmpty,
  }),

  management: z.object({
    max_hold_note:          nonEmpty,
    scale_note:             z.string().nullable().optional(),
    re_entry_note:          z.string().nullable().optional(),
    uncertainty_disclosure: nonEmpty,
  }),

  evidence: z.object({
    smc_read:           nonEmpty,
    regime_read:        nonEmpty,
    liquidity_read:     nonEmpty,
    trap_read:          nonEmpty,
    gamma_read:         nonEmpty,
    setup_read:         nonEmpty,
    supporting_factors: strArr,
    conflict_factors:   strArr,
    key_drivers:        z.array(z.string()).min(1).max(5),
  }),

  // AI may optionally return change context — pipeline computes authoritatively
  change_detection: z.object({
    change_type:      ChangeTypeSchema.optional(),
    material_changes: z.array(z.string()).optional(),
    what_changed:     z.array(z.string()).optional(),
  }).optional(),

  // AI may echo back audit fields — ignored in assembly
  audit: z.object({
    prompt_version: z.string().optional(),
    evidence_hash:  z.string().optional(),
  }).optional(),

}).superRefine((d, ctx) => {
  if (d.thesis_state === 'INVALIDATED' && d.action_posture !== 'NO_TRADE')
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['action_posture'],
      message: 'INVALIDATED must have NO_TRADE posture' });
  if (d.thesis_state === 'ACTIONABLE' && d.action_posture === 'NO_TRADE')
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['action_posture'],
      message: 'ACTIONABLE cannot have NO_TRADE posture' });
  if (d.action_posture === 'LONG'  && d.bias === 'bearish')
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['bias'],
      message: 'LONG posture conflicts with bearish bias — use mixed' });
  if (d.action_posture === 'SHORT' && d.bias === 'bullish')
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['bias'],
      message: 'SHORT posture conflicts with bullish bias — use mixed' });
});

export type RawAIThesis = z.infer<typeof RawAIThesisSchema>;

// ─── B. Final assembled thesis schema ────────────────────────────────────────
//
// Validated after assembly. Critical required fields use .min(1) — an empty
// string here means the assembly step lost data and must trigger fallback.

export const StructuredThesisSchema = z.object({
  thesis_id:  nonEmpty,
  symbol:     nonEmpty,
  timeframe:  nonEmpty,
  timestamp:  z.number().positive(),

  thesis_state:         ThesisStateSchema,
  action_posture:       ActionPostureSchema,
  is_actionable:        z.boolean(),
  bias:                 ThesisBiasSchema,
  conviction:           score,
  actionability:        score,

  // Classification — must be non-empty in a real thesis
  setup_classification: nonEmpty,
  setup_name:           nonEmpty,

  // Narrative — must have real content
  summary:              nonEmpty,
  decision:             nonEmpty,

  entry_plan: z.object({
    setup_type:          z.string(),      // derived from setup_classification — may be same
    entry_trigger:       nonEmpty,        // must have specific trigger
    entry_price_hint:    z.string().nullable(),
    confirmation_needed: nonEmpty,        // must describe what confirms entry
    timing_note:         z.string().nullable(),
  }),

  risk_plan: z.object({
    invalidation_level:  nonEmpty,        // must have specific level
    invalidation_reason: nonEmpty,        // must explain why
    stop_quality:        z.number().min(0).max(100),
    trap_conflict:       z.boolean(),
    regime_conflict:     z.boolean(),
    primary_risk_note:   nonEmpty,        // must describe top risk
  }),

  targets: z.object({
    target_1:     z.string().nullable(),
    target_2:     z.string().nullable(),
    risk_reward:  z.number().nullable(),
    target_logic: nonEmpty,              // must explain target rationale
  }),

  management: z.object({
    max_hold_note:          nonEmpty,    // must specify exit condition
    scale_note:             z.string().nullable(),
    re_entry_note:          z.string().nullable(),
    uncertainty_disclosure: nonEmpty,   // must acknowledge limits of analysis
  }),

  evidence: z.object({
    smc_read:           nonEmpty,
    regime_read:        nonEmpty,
    liquidity_read:     nonEmpty,
    trap_read:          nonEmpty,
    gamma_read:         nonEmpty,
    setup_read:         nonEmpty,
    supporting_factors: z.array(z.string()),
    conflict_factors:   z.array(z.string()),
    key_drivers:        z.array(z.string()),
    what_changed:       z.array(z.string()),
  }),

  confidence: z.object({
    structure_confidence:  score,
    regime_confidence:     score,
    liquidity_confidence:  score,
    setup_confidence:      score,
    risk_confidence:       score,
    alignment_confidence:  score,
    overall_confidence:    score,
    readiness_score:       score,
  }),

  change_detection: z.object({
    changed:               z.boolean(),
    change_type:           ChangeTypeSchema,
    material_changes:      z.array(z.string()),
    previous_thesis_state: ThesisStateSchema.nullable(),
    previous_posture:      ActionPostureSchema.nullable(),
    previous_bias:         ThesisBiasSchema.nullable(),
    conviction_delta:      z.number().nullable(),
  }),

  audit: z.object({
    thesis_id:         nonEmpty,
    generated_at:      z.number().positive(),
    model_id:          z.string(),
    source_version:    z.string(),
    prompt_version:    z.string(),
    schema_version:    z.string(),
    input_hash:        z.string(),
    gate_results:      z.array(z.any()),
    validation_status: ValidationStatusSchema,
    fallback_reason:   z.string().nullable(),
    generation_ms:     z.number().nullable(),
    repair_attempts:   z.number().min(0),
  }),

}).superRefine((d, ctx) => {
  if (d.is_actionable !== (d.thesis_state === 'ACTIONABLE'))
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['is_actionable'],
      message: 'is_actionable must equal (thesis_state === ACTIONABLE)' });
  if (d.thesis_state === 'ACTIONABLE' && d.audit.validation_status === 'GATE_DOWNGRADED')
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['thesis_state'],
      message: 'ACTIONABLE cannot coexist with GATE_DOWNGRADED status' });
  // FALLBACK theses must never claim is_actionable
  if (d.audit.validation_status === 'FALLBACK' && d.is_actionable)
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['is_actionable'],
      message: 'FALLBACK thesis cannot be is_actionable' });
});

// Inferred type from the final schema — used as the return type of validateFinalThesis
export type AssembledThesis = z.infer<typeof StructuredThesisSchema>;

// ─── Validation helpers ───────────────────────────────────────────────────────

export function validateRawAIResponse(raw: unknown): {
  ok: true;  data: RawAIThesis;
} | {
  ok: false; errors: string[];
} {
  const result = RawAIThesisSchema.safeParse(raw);
  if (result.success) return { ok: true, data: result.data };
  return {
    ok:     false,
    errors: result.error.issues.map(i => `${i.path.join('.')}: ${i.message}`),
  };
}

export function validateFinalThesis(thesis: unknown): {
  ok: true;  data: AssembledThesis;
} | {
  ok: false; errors: string[];
} {
  const result = StructuredThesisSchema.safeParse(thesis);
  if (result.success) return { ok: true, data: result.data };
  return {
    ok:     false,
    errors: result.error.issues.map(i => `${i.path.join('.')}: ${i.message}`),
  };
}
